/**
 * Google Drive connector — browser-only (Google Picker API + Google Identity Services).
 *
 * Lets a user pick CV files OR an entire folder from their Drive; folders are
 * walked recursively and every supported CV inside is downloaded into a `File[]`
 * ready to be fed to the existing `uploadCandidates` pipeline.
 *
 * Requires two public env vars (graceful no-op if missing):
 *   - VITE_GOOGLE_CLIENT_ID  : OAuth 2.0 Web client id
 *   - VITE_GOOGLE_API_KEY    : API key (Picker developer key)
 *   - VITE_GOOGLE_APP_ID     : (optional) GCP project number, improves Picker UX
 */

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

// Same set the create-cv edge function / useUpload accepts.
const CV_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
const APP_ID = import.meta.env.VITE_GOOGLE_APP_ID as string | undefined;

export const isDriveConfigured = (): boolean => Boolean(CLIENT_ID && API_KEY);

interface DriveFileMeta {
  id: string;
  name: string;
  mimeType: string;
}

// --- Minimal typings for the Google SDK globals we use --------------------

interface TokenResponse {
  access_token: string;
  expires_in?: number;
  error?: string;
}

interface TokenClient {
  requestAccessToken(opts: { prompt: string }): void;
}

interface PickerDoc {
  id: string;
  name: string;
  mimeType: string;
}

interface PickerData {
  action: string;
  docs?: PickerDoc[];
}

interface DocsViewObj {
  setIncludeFolders(v: boolean): DocsViewObj;
  setSelectFolderEnabled(v: boolean): DocsViewObj;
  setMimeTypes(v: string): DocsViewObj;
}

interface PickerBuilderObj {
  enableFeature(f: string): PickerBuilderObj;
  setDeveloperKey(k: string): PickerBuilderObj;
  setOAuthToken(t: string): PickerBuilderObj;
  addView(v: DocsViewObj): PickerBuilderObj;
  setTitle(t: string): PickerBuilderObj;
  setAppId(id: string): PickerBuilderObj;
  setCallback(cb: (data: PickerData) => void): PickerBuilderObj;
  build(): { setVisible(v: boolean): void };
}

interface GoogleGlobals {
  accounts: {
    oauth2: {
      initTokenClient(cfg: {
        client_id: string;
        scope: string;
        callback: (resp: TokenResponse) => void;
      }): TokenClient;
    };
  };
  picker: {
    ViewId: { DOCS: string };
    Feature: { MULTISELECT_ENABLED: string };
    Action: { PICKED: string; CANCEL: string };
    DocsView: new (viewId: string) => DocsViewObj;
    PickerBuilder: new () => PickerBuilderObj;
  };
}

interface GapiGlobal {
  load(api: string, cfg: { callback: () => void; onerror?: () => void }): void;
}

const getGapi = (): GapiGlobal | undefined => (window as unknown as { gapi?: GapiGlobal }).gapi;
const getGoogle = (): GoogleGlobals | undefined => (window as unknown as { google?: GoogleGlobals }).google;

// --- Script / SDK loading (memoized) -------------------------------------

const loadedScripts = new Map<string, Promise<void>>();

const loadScript = (src: string): Promise<void> => {
  const existing = loadedScripts.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Échec du chargement du script Google : ${src}`));
    document.head.appendChild(script);
  });

  loadedScripts.set(src, promise);
  return promise;
};

let pickerReady = false;
const ensurePicker = async (): Promise<void> => {
  await loadScript('https://apis.google.com/js/api.js');
  if (pickerReady) return;
  await new Promise<void>((resolve, reject) => {
    const gapi = getGapi();
    if (!gapi) {
      reject(new Error('gapi indisponible après chargement'));
      return;
    }
    gapi.load('picker', { callback: () => resolve(), onerror: () => reject(new Error('Échec du chargement du module Picker')) });
  });
  pickerReady = true;
};

// --- OAuth token (Google Identity Services) ------------------------------

let cachedToken: { value: string; expiresAt: number } | null = null;

const getAccessToken = async (): Promise<string> => {
  if (cachedToken && cachedToken.expiresAt > Date.now() + 60_000) {
    return cachedToken.value;
  }
  await loadScript('https://accounts.google.com/gsi/client');

  return new Promise<string>((resolve, reject) => {
    const google = getGoogle();
    if (!google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services indisponible'));
      return;
    }
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID as string,
      scope: DRIVE_SCOPE,
      callback: (resp: TokenResponse) => {
        if (resp.error) {
          reject(new Error(`Autorisation Google refusée : ${resp.error}`));
          return;
        }
        cachedToken = {
          value: resp.access_token,
          expiresAt: Date.now() + (Number(resp.expires_in) || 3600) * 1000,
        };
        resolve(resp.access_token);
      },
    });
    tokenClient.requestAccessToken({ prompt: cachedToken ? '' : 'consent' });
  });
};

// --- Picker --------------------------------------------------------------

const showPicker = (token: string): Promise<PickerDoc[]> => {
  return new Promise((resolve) => {
    const google = getGoogle();
    if (!google?.picker) {
      resolve([]);
      return;
    }
    const picker = google.picker;

    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes([...CV_MIME_TYPES, FOLDER_MIME].join(','));

    const builder = new picker.PickerBuilder()
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(API_KEY as string)
      .setOAuthToken(token)
      .addView(view)
      .setTitle('Sélectionnez des CV ou un dossier')
      .setCallback((data: PickerData) => {
        if (data.action === picker.Action.PICKED) {
          resolve(data.docs || []);
        } else if (data.action === picker.Action.CANCEL) {
          resolve([]);
        }
      });

    if (APP_ID) builder.setAppId(APP_ID);
    builder.build().setVisible(true);
  });
};

// --- Drive REST: recursive folder listing + download ---------------------

const listFolderFiles = async (folderId: string, token: string): Promise<DriveFileMeta[]> => {
  const out: DriveFileMeta[] = [];
  let pageToken: string | undefined;

  do {
    const q = encodeURIComponent(`'${folderId}' in parents and trashed = false`);
    const fields = encodeURIComponent('nextPageToken, files(id,name,mimeType)');
    const url =
      `https://www.googleapis.com/drive/v3/files?q=${q}&fields=${fields}&pageSize=1000` +
      (pageToken ? `&pageToken=${pageToken}` : '');

    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Lecture du dossier Drive échouée : ${res.statusText}`);

    const json = await res.json();
    for (const f of json.files || []) {
      if (f.mimeType === FOLDER_MIME) {
        out.push(...(await listFolderFiles(f.id, token)));
      } else if (CV_MIME_TYPES.includes(f.mimeType)) {
        out.push(f);
      }
    }
    pageToken = json.nextPageToken;
  } while (pageToken);

  return out;
};

const downloadFile = async (meta: DriveFileMeta, token: string): Promise<File> => {
  const url = `https://www.googleapis.com/drive/v3/files/${meta.id}?alt=media`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Téléchargement échoué : ${meta.name}`);
  const blob = await res.blob();
  return new File([blob], meta.name, { type: meta.mimeType });
};

// --- Public API ----------------------------------------------------------

/**
 * Opens the Google Drive Picker, resolves any selected folders into their CV
 * files, downloads everything, and returns the resulting `File[]`.
 * @param onProgress optional callback fired during the download phase.
 */
export const pickDriveFiles = async (
  onProgress?: (current: number, total: number) => void,
): Promise<File[]> => {
  if (!isDriveConfigured()) {
    throw new Error('Google Drive non configuré (VITE_GOOGLE_CLIENT_ID / VITE_GOOGLE_API_KEY).');
  }

  await ensurePicker();
  const token = await getAccessToken();
  const docs = await showPicker(token);
  if (docs.length === 0) return [];

  // Resolve folders → flat list of CV file metas, de-duplicated by id.
  const seen = new Set<string>();
  const metas: DriveFileMeta[] = [];

  for (const doc of docs) {
    if (doc.mimeType === FOLDER_MIME) {
      for (const f of await listFolderFiles(doc.id, token)) {
        if (!seen.has(f.id)) {
          seen.add(f.id);
          metas.push(f);
        }
      }
    } else if (CV_MIME_TYPES.includes(doc.mimeType)) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        metas.push({ id: doc.id, name: doc.name, mimeType: doc.mimeType });
      }
    }
  }

  const files: File[] = [];
  for (let i = 0; i < metas.length; i++) {
    onProgress?.(i + 1, metas.length);
    files.push(await downloadFile(metas[i], token));
  }
  return files;
};
